import Card from 'components/Layout/Card'
import PageHeader from 'components/Layout/PageHeader'
import Pagination from 'components/Pagination'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './Broadsign.scss'
import Input from 'components/Form/Input'
import Select, { SelectOption } from 'components/Form/Select'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import TsxUtils from '../../utils/tsx'
import FillingSpinner, { FillingSpinnerType } from '../../components/FillingSpinner'
import BroadSignCreationMediaTable from './components/BroadSignCreationMediaTable/BroadSignCreationMediaTable'
import debounce from 'lodash/debounce'
import BroadSignService from './services/broad-sign.service'
import BroadSignCreationModal from './containers/BroadSignCreationModal'
import { broadSignActions } from './store/broad-sign-slice'
import { BroadSignCreationModel } from '../../api/broadSign/models/broad-sign-creation.model'
import { BroadSignFiltersModel } from '../../api/broadSign/models/broad-sign-filters.model'
import DateUtils from '../../utils/date'
import { BROADSIGN_CREATIONS_PER_PAGE, DATE_FORMAT } from '../../constant'
import { MediaFormat } from '../../types/campaign'
import IconAlert from '../../components/Icon/IconAlert'
import Tooltip from '../../components/Tooltip'

const creationTitle = (creation: BroadSignCreationModel): string =>
  creation.details || creation.file.filename

const mediaFormatLabelKey = (mediaFormat: string): string =>
  `mediaType.${MediaFormat[mediaFormat as keyof typeof MediaFormat]}`

const Broadsign: React.FC = () => {
  const debounceTime = 2000
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    loading,
    mediaTreeLoading,
    pageNumber,
    totalItemsCount,
    data,
    mediaTree,
    acceptances,
    dropdowns,
    filters,
    selectedCreation,
  } = useSelector((state: RootState) => state.broadSign)

  const [previewedCreationId, setPreviewedCreationId] = useState<string | null>(null)
  const [hoveredAlertCreationId, setHoveredAlertCreationId] = useState<string | null>(null)

  const previewedCreation = data.find(creation => creation.id === previewedCreationId) ?? null

  const mediaFormatOptions = dropdowns.mediaFormats.map((mediaFormat): SelectOption => {
    return {
      value: mediaFormat,
      label: t(mediaFormatLabelKey(mediaFormat)),
    }
  })

  const updateFilters = (changedFilters: Partial<BroadSignFiltersModel>) => {
    void dispatch(broadSignActions.setFilters({ ...filters, ...changedFilters }))
  }

  const debouncedFetchBroadSignCreations = useCallback(
    debounce(BroadSignService.fetchBroadSignCreations, debounceTime),
    []
  )

  useEffect(() => {
    const firstPage = 1

    // the fetch itself is debounced, so flag loading up front to cover the waiting time
    void dispatch(broadSignActions.setLoading(true))
    setPreviewedCreationId(null)
    void debouncedFetchBroadSignCreations(filters, firstPage)
  }, [filters])

  useEffect(() => {
    setPreviewedCreationId(currentId =>
      currentId && data.some(creation => creation.id === currentId) ? currentId : null
    )
  }, [data])

  useEffect(() => {
    if (!previewedCreationId) {
      void dispatch(broadSignActions.setMediaTree([]))
      void dispatch(broadSignActions.setAcceptances([]))

      return
    }

    void BroadSignService.fetchCreationMediaTree(previewedCreationId)
    void BroadSignService.fetchCreationAcceptances(previewedCreationId)
  }, [previewedCreationId])

  useEffect(() => {
    return () => {
      dispatch(broadSignActions.resetState())
    }
  }, [])

  const setPageNumber = (page: number) => {
    void BroadSignService.fetchBroadSignCreations(filters, page)
  }

  const closeCreationModal = () => {
    void dispatch(broadSignActions.setSelectedCreation(undefined))
  }

  return (
    <div className={'Broadsign__container'}>
      <PageHeader>{t('broadSign.acceptanceTitle')}</PageHeader>

      <div className='Broadsign__layout'>
        <div className='Broadsign__sidebar'>
          <Card className='Broadsign__filters'>
            <Input
              id='text'
              title={t('common.search')}
              value={filters.text}
              onChange={text => updateFilters({ text })}
              placeholder={t('broadSign.searchPlaceholder')}
              dataCy={'broadsign-search-input'}
            />
            <Select
              id='mediaFormats'
              title={t('broadSign.kind')}
              value={filters.mediaFormats[0] ?? null}
              options={mediaFormatOptions}
              onChange={val => updateFilters({ mediaFormats: val ? [val as string] : [] })}
              clearable
              dataCy={'broadsign-media-formats-select'}
            />
          </Card>

          <Card className='Broadsign__creations-list'>
            <div className='Broadsign__creations-list-title'>{t('broadSign.selectCreation')}</div>
            {loading ? (
              <FillingSpinner
                type={FillingSpinnerType.STANDARD}
                className='Broadsign__creations-list-spinner'
              />
            ) : (
              <>
                {!data.length && (
                  <div className='Broadsign__creations-list-no-data'>{t('common.noData')}</div>
                )}
                {data.map(creation => (
                  <div
                    key={creation.id}
                    className={
                      'Broadsign__creation-item' +
                      TsxUtils.extraStyle(
                        previewedCreationId === creation.id,
                        'Broadsign__creation-item--active'
                      )
                    }
                    onClick={() => setPreviewedCreationId(creation.id)}
                  >
                    <div className='Broadsign__creation-item-header'>
                      <div className='Broadsign__creation-item-title'>
                        {creationTitle(creation)}
                      </div>
                      {creation.usedBuildingsNotSentCount > 0 && (
                        <div
                          className='Broadsign__creation-item-alert'
                          onMouseEnter={() => setHoveredAlertCreationId(creation.id)}
                          onMouseLeave={() => setHoveredAlertCreationId(null)}
                        >
                          <IconAlert />
                          {hoveredAlertCreationId === creation.id && (
                            <Tooltip>
                              {t('broadSign.usedBuildingsNotSent', {
                                number: creation.usedBuildingsNotSentCount,
                              })}
                            </Tooltip>
                          )}
                        </div>
                      )}
                    </div>
                    <div className='Broadsign__creation-item-desc'>
                      {t('broadSign.id')}: {creation.broadsignId}
                    </div>
                    <div className='Broadsign__creation-item-desc'>
                      {t('broadSign.createdAt')}:{' '}
                      {DateUtils.parseAndFormat(creation.createdAt, DATE_FORMAT)}
                    </div>
                  </div>
                ))}
                <Pagination
                  activePage={pageNumber}
                  itemsCountPerPage={BROADSIGN_CREATIONS_PER_PAGE}
                  totalItemsCount={totalItemsCount}
                  pageRangeDisplayed={3}
                  onChange={setPageNumber}
                />
              </>
            )}
          </Card>

          {previewedCreation && (
            <Card className='Broadsign__preview'>
              <div className='Broadsign__preview-title'>{t('broadSign.creationPreview')}</div>
              <div className='Broadsign__preview-image'>
                <img
                  src={previewedCreation.thumbnailUrl}
                  alt={previewedCreation.file.filename}
                />
              </div>
              <div className='Broadsign__preview-details'>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.name')}</span>
                  <span className='Broadsign__preview-value'>
                    {creationTitle(previewedCreation)}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.resolution')}</span>
                  <span className='Broadsign__preview-value'>
                    {previewedCreation.width + 'x' + previewedCreation.height + 'px'}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.id')}</span>
                  <span className='Broadsign__preview-value'>{previewedCreation.broadsignId}</span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.kind')}</span>
                  <span className='Broadsign__preview-value'>
                    {previewedCreation.mediaFormat
                      ? t(mediaFormatLabelKey(previewedCreation.mediaFormat))
                      : '-'}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.status')}</span>
                  <span className='Broadsign__preview-value'>
                    {t(`broadSign.${previewedCreation.status}`)}
                  </span>
                </div>
              </div>
            </Card>
          )}
        </div>

        <Card className='Broadsign__content'>
          {previewedCreation ? (
            // the tables carry the spinner themselves, reloading the tree must not take them off screen
            <BroadSignCreationMediaTable
              mediaTree={mediaTree}
              acceptances={acceptances}
              creationId={previewedCreation.id}
              creationName={creationTitle(previewedCreation)}
              loading={mediaTreeLoading}
            />
          ) : (
            // the tables belong to a creation, until one is picked there is nothing for them to show
            <div className='Broadsign__empty'>
              <p>{t('broadSign.noCreationSelected')}</p>
            </div>
          )}
          <BroadSignCreationModal
            creation={selectedCreation}
            closeClick={closeCreationModal}
          />
        </Card>
      </div>
    </div>
  )
}

export default Broadsign
